import { api } from './api';

/**
 * Menu Data Service - Handles caching and management of menu data
 * Loads all data upfront and stores in memory for the session
 */
class MenuDataService {
    constructor() {
        this.data = {
            categories: [],
            items: [],
            modifiers: {},
            merchant: null,
            taxConfig: {
                rates: [],
                lookup: {},
                defaults: {
                    ids: [],
                    names: [],
                    totalRate: 0.0825,
                    totalFlatAmount: 0,
                    label: 'DEFAULT'
                },
                cacheMeta: {},
                updatedAt: null,
                isFallback: true
            },
            isLoaded: false,
            isLoading: false,
            loadError: null
        };

        // Track last background refresh per category to throttle API calls
        this.lastCategoryFetch = {}; // { [categoryId]: timestamp }
        this.CATEGORY_REFRESH_INTERVAL_MS = 5 * 60 * 1000; // 5 minutes

        // Browser caching disabled - server handles all caching
        console.log('📱 Browser caching disabled - using server cache only');
    }

    /**
     * Initialize and load all menu data
     */
    async initialize() {
        if (this.data.isLoading) {
            console.log('🔄 Data already loading, waiting...');
            return this.waitForLoad();
        }

        if (this.data.isLoaded) {
            console.log('✅ Data already loaded, returning cached data');
            return this.data;
        }

        console.log('🚀 Loading fresh menu data from server...');
        this.data.isLoading = true;
        this.data.loadError = null;

        try {
            // 🚀 NEW: Try comprehensive endpoint first for better performance
            try {
                console.log('⚡ Attempting fast load via comprehensive endpoint...');
                const [comprehensiveResponse, taxLoadPromise] = await Promise.all([
                    api.getAllMenuData(),
                    this.loadTaxConfiguration()
                ]);

                if (comprehensiveResponse.success && comprehensiveResponse.data) {
                    // Store all data from comprehensive endpoint
                    this.data.categories = comprehensiveResponse.data.categories || [];
                    this.data.items = comprehensiveResponse.data.items || [];
                    this.data.merchant = comprehensiveResponse.data.merchant || null;

                    if (comprehensiveResponse.meta?.taxDefaults) {
                        this.applyTaxDefaults(comprehensiveResponse.meta.taxDefaults);
                    }

                    console.log(`⚡ Fast load complete (no modifiers prefetch): ${this.data.categories.length} categories, ${this.data.items.length} items`);

                    this.data.isLoaded = true;
                    this.data.isLoading = false;

                    await taxLoadPromise.catch(error => {
                        console.warn('⚠️ Tax configuration load failed after comprehensive fetch:', error?.message || error);
                    });

                    console.log('✅ All menu data loaded successfully (fast mode)');
                    return this.data;
                }
            } catch (comprehensiveError) {
                console.log('⚠️ Comprehensive endpoint failed, falling back to individual calls:', comprehensiveError.message);
            }

            // 🔄 FALLBACK: Original individual API calls for backward compatibility
            console.log('🔄 Loading via individual API calls...');
            const [categoriesResponse, itemsResponse, merchantResponse] = await Promise.all([
                api.getCategories(),
                api.getItems(),
                api.getMerchantInfo()
            ]);

            // Store categories
            this.data.categories = categoriesResponse.data.categories || [];
            console.log(`📂 Loaded ${this.data.categories.length} categories`);

            // Store items
            this.data.items = itemsResponse.data.items || [];
            console.log(`🍽️ Loaded ${this.data.items.length} items`);

            // Store merchant info
            this.data.merchant = merchantResponse.data.merchant || null;
            console.log(`🏪 Loaded merchant info`);
            console.log('⏭️ Skipping modifiers prefetch (will load on demand)');

            await this.loadTaxConfiguration();

            this.data.isLoaded = true;
            this.data.isLoading = false;

            console.log('✅ All menu data loaded successfully (fallback mode)');
            return this.data;

        } catch (error) {
            console.error('❌ Error loading menu data:', error);
            this.data.loadError = error;
            this.data.isLoading = false;
            throw error;
        }
    }

    /**
     * Load modifiers for items that have modifier groups
     */
    async loadModifiers() {
        const itemsWithModifiers = this.data.items.filter(item => {
            // Handle both old and new structures
            return (Array.isArray(item.modifierGroups) && item.modifierGroups.length > 0) ||
                (item.modifierGroups && Array.isArray(item.modifierGroups.elements) && item.modifierGroups.elements.length > 0);
        });

        if (itemsWithModifiers.length === 0) {
            console.log('ℹ️ No items with modifiers found');
            return;
        }

        console.log(`🔧 Loading modifiers for ${itemsWithModifiers.length} items...`);

        // Load modifiers in batches to avoid rate limits
        const batchSize = 5;
        for (let i = 0; i < itemsWithModifiers.length; i += batchSize) {
            const batch = itemsWithModifiers.slice(i, i + batchSize);

            await Promise.all(batch.map(async (item) => {
                try {
                    const modifiersResponse = await api.getItemModifiers(item.id);
                    this.data.modifiers[item.id] = modifiersResponse.data.modifiers || {};
                } catch (error) {
                    console.warn(`⚠️ Failed to load modifiers for item ${item.id}:`, error);
                    this.data.modifiers[item.id] = {};
                }
            }));

            // Small delay between batches to respect rate limits
            if (i + batchSize < itemsWithModifiers.length) {
                await new Promise(resolve => setTimeout(resolve, 100));
            }
        }

        console.log(`✅ Loaded modifiers for ${Object.keys(this.data.modifiers).length} items`);
    }

    /**
     * Wait for current loading to complete
     */
    async waitForLoad() {
        return new Promise((resolve, reject) => {
            const checkLoading = () => {
                if (!this.data.isLoading) {
                    if (this.data.loadError) {
                        reject(this.data.loadError);
                    } else {
                        resolve(this.data);
                    }
                } else {
                    setTimeout(checkLoading, 100);
                }
            };
            checkLoading();
        });
    }

    /**
     * Check if cached data is still valid
     */
    isDataValid() {
        // Disabled browser caching - always fetch fresh data from server cache
        // The server already handles caching from Clover POS efficiently
        return false;
    }

    /**
     * Get all categories
     */
    getCategories() {
        return this.data.categories;
    }

    /**
     * Get catering categories
     */
    getCateringCategories() {
        return this.data.categories.filter(cat =>
            cat.name.toLowerCase().includes('catering') ||
            cat.name.toLowerCase().includes('bulk')
        );
    }

    /**
     * Get items by category ID
     * Now uses direct API call to get complete category data
     */
    async getItemsByCategory(categoryId) {
        // 1. Immediate cache filter (fast path)
        const cached = this.data.items.filter(item => item.categoryIds && item.categoryIds.includes(categoryId));
        const now = Date.now();
        const lastFetch = this.lastCategoryFetch[categoryId] || 0;
        const stale = (now - lastFetch) > this.CATEGORY_REFRESH_INTERVAL_MS;

        if (cached.length > 0) {
            // Return cached immediately
            // Kick off background refresh if stale
            if (stale) {
                this.lastCategoryFetch[categoryId] = now; // prevent stampede
                (async () => {
                    try {
                        const response = await api.getItemsByCategory(categoryId);
                        if (response.success && response.data && Array.isArray(response.data.items)) {
                            console.log(`♻️ BG refresh: category ${categoryId} updated (${response.data.items.length} items)`);
                            // Merge: replace any items with same id; add new ones
                            const incomingMap = new Map(response.data.items.map(i => [i.id, i]));
                            this.data.items = this.data.items.map(existing => incomingMap.get(existing.id) || existing);
                            // Add any new items not in cache yet
                            response.data.items.forEach(it => {
                                if (!this.data.items.find(e => e.id === it.id)) this.data.items.push(it);
                            });
                        }
                    } catch (e) {
                        console.warn(`⚠️ BG refresh failed for category ${categoryId}:`, e.message);
                    }
                })();
            }
            return cached;
        }

        // 2. Cache miss: perform API fetch (blocking)
        try {
            const response = await api.getItemsByCategory(categoryId);
            if (response.success && response.data && Array.isArray(response.data.items)) {
                console.log(`📦 API fetch (cache miss) loaded ${response.data.items.length} items for category ${categoryId}`);
                // Merge into global items list
                const existingIds = new Set(this.data.items.map(i => i.id));
                response.data.items.forEach(it => { if (!existingIds.has(it.id)) this.data.items.push(it); });
                this.lastCategoryFetch[categoryId] = Date.now();
                return response.data.items;
            }
        } catch (error) {
            console.warn(`⚠️ Failed API fetch for category ${categoryId} after cache miss:`, error);
        }

        // 3. Still nothing: return empty array
        return [];
    }

    /**
     * Get item by ID
     */
    getItemById(itemId) {
        return this.data.items.find(item => item.id === itemId);
    }

    /**
     * Get modifiers for item
     */
    getItemModifiers(itemId) {
        console.log('🔍 getItemModifiers called for itemId:', itemId);
        console.log('🔍 Available modifiers keys:', Object.keys(this.data.modifiers));
        console.log('🔍 Modifiers for this item:', this.data.modifiers[itemId]);

        // Also check if the item has modifierGroups in the item data
        const item = this.data.items.find(i => i.id === itemId);
        console.log('🔍 Item found:', item?.name);
        console.log('🔍 Item modifierGroups:', item?.modifierGroups);

        // Special debug for Single Bagel
        if (item && item.name === 'Single Bagel') {
            console.log('🥯 SINGLE BAGEL DEBUG:');
            console.log('🥯 Cached modifiers exist:', !!this.data.modifiers[itemId]);
            console.log('🥯 Cached modifiers content:', this.data.modifiers[itemId]);
            console.log('🥯 Item has modifierGroups:', !!item.modifierGroups);

            // If no cached modifiers, try to fetch fresh from API
            if (!this.data.modifiers[itemId] || Object.keys(this.data.modifiers[itemId]).length === 0) {
                console.log('🥯 No cached modifiers found, will fetch fresh from API');
                // Note: This is async, but getItemModifiers is sync. Consider refactoring.
            }
        }

        // If not cached, trigger async lazy load (fire & forget) so next access has data
        if (!this.data.modifiers[itemId]) {
            (async () => {
                try {
                    const modifiersResponse = await api.getItemModifiers(itemId);
                    this.data.modifiers[itemId] = modifiersResponse.data.modifiers || {};
                    console.log(`🌙 Lazy-loaded modifiers for item ${itemId}`);
                } catch (e) {
                    console.warn(`⚠️ Lazy load failed for modifiers of item ${itemId}:`, e.message);
                }
            })();
        }
        return this.data.modifiers[itemId] || {};
    }

    /**
     * Force refresh modifiers for a specific item
     */
    async refreshItemModifiers(itemId) {
        console.log('🔄 Force refreshing modifiers for item:', itemId);
        try {
            const modifiersResponse = await api.getItemModifiers(itemId);
            this.data.modifiers[itemId] = modifiersResponse.data.modifiers || {};
            console.log('✅ Refreshed modifiers for item:', itemId, this.data.modifiers[itemId]);
            return this.data.modifiers[itemId];
        } catch (error) {
            console.warn(`⚠️ Failed to refresh modifiers for item ${itemId}:`, error);
            return {};
        }
    }

    /**
     * Optional bulk prefetch (e.g., for high-traffic items) - not invoked by default
     */
    async prefetchModifiersForItems(itemIds = []) {
        const unique = [...new Set(itemIds)].filter(id => !this.data.modifiers[id]);
        if (unique.length === 0) return;
        console.log(`🚚 Prefetching modifiers for ${unique.length} items (on-demand)`);
        await Promise.all(unique.map(async id => {
            try {
                const res = await api.getItemModifiers(id);
                this.data.modifiers[id] = res.data.modifiers || {};
            } catch (e) {
                this.data.modifiers[id] = this.data.modifiers[id] || {};
                console.warn(`⚠️ Prefetch failed for item ${id}:`, e.message);
            }
        }));
        console.log('✅ Prefetch complete');
    }
    /**
     * Get merchant info
     */
    getMerchantInfo() {
        return this.data.merchant;
    }

    /**
     * Get tax configuration
     */
    getTaxConfig() {
        return this.data.taxConfig;
    }

    /**
     * Apply tax defaults from backend meta
     */
    applyTaxDefaults(defaults) {
        if (!defaults) return;

        this.data.taxConfig = {
            ...this.data.taxConfig,
            defaults: {
                ids: defaults.ids || [],
                names: defaults.names || [],
                totalRate: typeof defaults.totalRate === 'number' ? defaults.totalRate : this.data.taxConfig.defaults.totalRate,
                totalFlatAmount: typeof defaults.totalFlatAmount === 'number' ? defaults.totalFlatAmount : this.data.taxConfig.defaults.totalFlatAmount,
                label: defaults.label || this.data.taxConfig.defaults.label
            },
            isFallback: false,
            updatedAt: new Date().toISOString()
        };
    }

    /**
     * Load comprehensive tax configuration from backend
     */
    async loadTaxConfiguration() {
        try {
            const response = await api.getTaxRates();
            if (!response || !response.success || !response.data) {
                throw new Error('Invalid tax configuration response');
            }

            const { rates = [], defaults = {}, cache = {} } = response.data;

            const lookup = {};
            rates.forEach(rate => {
                if (rate && rate.id) {
                    lookup[rate.id] = rate;
                }
            });

            this.data.taxConfig = {
                rates,
                lookup,
                defaults: {
                    ids: defaults.ids || [],
                    names: defaults.names || [],
                    totalRate: typeof defaults.totalRate === 'number' ? defaults.totalRate : (this.data.taxConfig.defaults?.totalRate || 0.0825),
                    totalFlatAmount: typeof defaults.totalFlatAmount === 'number' ? defaults.totalFlatAmount : (this.data.taxConfig.defaults?.totalFlatAmount || 0),
                    label: cache.defaultLabel || defaults.names?.join(' + ') || this.data.taxConfig.defaults?.label || 'DEFAULT'
                },
                cacheMeta: cache,
                updatedAt: new Date().toISOString(),
                isFallback: false
            };
            console.log(`🧾 Tax configuration loaded: ${rates.length} rates, default rate = ${this.data.taxConfig.defaults.totalRate}`);
        } catch (error) {
            console.warn('⚠️ Failed to load tax configuration:', error?.message || error);
            this.data.taxConfig = {
                ...this.data.taxConfig,
                updatedAt: this.data.taxConfig.updatedAt || new Date().toISOString(),
                isFallback: true
            };
        }
    }

    /**
     * Check if item is available
     */
    async checkItemAvailability(itemId) {
        try {
            // This still needs to be real-time, so we call the API
            const response = await api.checkItemAvailability(itemId);
            return response.data;
        } catch (error) {
            console.warn(`⚠️ Failed to check availability for item ${itemId}:`, error);
            return { available: true, quantity: null }; // Default to available
        }
    }

    /**
     * Search items by name or description
     */
    searchItems(query) {
        const searchTerm = query.toLowerCase();
        return this.data.items.filter(item =>
            item.name.toLowerCase().includes(searchTerm) ||
            (item.description && item.description.toLowerCase().includes(searchTerm))
        );
    }

    /**
     * Get items by multiple category IDs
     */
    getItemsByCategories(categoryIds) {
        return this.data.items.filter(item =>
            item.categoryIds && item.categoryIds.some(catId => categoryIds.includes(catId))
        );
    }

    // NEW: Return all loaded items (already populated during initialize)
    getAllItems() {
        return this.data.items;
    }

    /**
     * Clear cache and force reload
     */
    async refresh() {
        this.data.isLoaded = false;
        return this.initialize();
    }

    /**
     * Get loading state
     */
    getLoadingState() {
        return {
            isLoading: this.data.isLoading,
            isLoaded: this.data.isLoaded,
            error: this.data.loadError
        };
    }

    /**
     * Force refresh all menu data (clear cache and reload)
     */
    async forceRefresh() {
        console.log('🔄 Force refreshing all menu data...');
        this.data = {
            categories: [],
            items: [],
            modifiers: {},
            merchant: null,
            isLoaded: false,
            isLoading: false,
            loadError: null
        };

        return await this.initialize();
    }

    /**
     * Get all cached data (for debugging)
     */
    getDebugInfo() {
        return {
            categoriesCount: this.data.categories.length,
            itemsCount: this.data.items.length,
            modifiersCount: Object.keys(this.data.modifiers).length,
            isLoaded: this.data.isLoaded,
            isLoading: this.data.isLoading,
            singleBagelModifiers: this.data.modifiers['KE88KZ1SWXSP4'],
            loadError: this.data.loadError
        };
    }

    /**
     * NEW: Get current pricing for an item by ID (for consistent pricing)
     */
    getItemPricing(itemId) {
        const item = this.data.items.find(item => item.id === itemId);
        if (!item) {
            console.warn(`⚠️ Item ${itemId} not found in cached menu data`);
            return null;
        }

        // Handle both old and new modifierGroups structures
        let modifierGroups = [];

        if (Array.isArray(item.modifierGroups)) {
            // Old structure: direct array
            modifierGroups = item.modifierGroups;
        } else if (item.modifierGroups && Array.isArray(item.modifierGroups.elements)) {
            // New structure: object with elements array
            modifierGroups = item.modifierGroups.elements;
        } else if (item.modifierGroups) {
            console.warn(`⚠️ Item ${itemId} has unexpected modifierGroups structure:`, typeof item.modifierGroups, item.modifierGroups);
            modifierGroups = [];
        }

        return {
            id: item.id,
            name: item.name,
            basePrice: item.price || 0,
            modifiers: modifierGroups.map(group => ({
                groupId: group.id,
                groupName: group.name,
                elements: (group.elements || group.modifiers?.elements || [])?.map(mod => ({
                    id: mod.id,
                    name: mod.name,
                    price: mod.price || 0
                })) || []
            })) || []
        };
    }

    /**
     * NEW: Calculate current price for an item with specific modifiers
     */
    calculateCurrentPrice(itemId, selectedModifiers = []) {
        const itemPricing = this.getItemPricing(itemId);
        if (!itemPricing) return 0;

        let totalPrice = itemPricing.basePrice;

        // Add modifier prices
        selectedModifiers.forEach(selectedMod => {
            // Find the modifier in the pricing data
            for (const group of itemPricing.modifiers) {
                const modifier = group.elements.find(mod =>
                    mod.id === selectedMod.id || mod.name === selectedMod.name
                );
                if (modifier) {
                    const quantity = selectedMod.quantity || 1;
                    totalPrice += modifier.price * quantity;
                    break;
                }
            }
        });

        return totalPrice;
    }

    /**
     * NEW: Validate cart item pricing against current menu data
     */
    validateCartItemPricing(cartItem) {
        const currentPrice = this.calculateCurrentPrice(cartItem.itemId || cartItem.id, cartItem.modifiers);
        const storedPrice = cartItem.totalPrice || cartItem.price || 0;

        return {
            isValid: currentPrice === storedPrice,
            currentPrice,
            storedPrice,
            difference: currentPrice - storedPrice,
            needsUpdate: currentPrice !== storedPrice
        };
    }
}

// Create singleton instance
export const menuDataService = new MenuDataService();
export default menuDataService;
